import type RouteInfo from '@ember/routing/route-info'
import type { RouteInfoWithAttributes } from '@ember/routing/router-service'

export type RouteInfoWithMetadataRecord<T extends Record<string, unknown>> = {
  metadata: T
} & RouteInfo

/**
 * Type predicate that checks if the given RouteInfo has a metadata record.
 * This is useful for determining if the route has additional metadata
 * that can be used for various purposes, such as analytics or feature flags.
 *
 * Metadata is added to a route via Route#buildRouteInfoMetadata()
 *
 * @param maybeMetadata - The route info to check.
 * @returns True if the route info has a metadata record, false otherwise.
 */
export function hasMetadataRecord(
  maybeMetadata: RouteInfo,
): maybeMetadata is RouteInfoWithMetadataRecord<Record<string, unknown>> {
  return (
    typeof maybeMetadata?.metadata === 'object' &&
    maybeMetadata.metadata !== null &&
    !Array.isArray(maybeMetadata.metadata)
  )
}

// Returns true if the current route or any of its parent routes match the predicate
export function anyRoute(
  currentRoute: RouteInfo | RouteInfoWithAttributes | null,
  predicate: (info: RouteInfo) => boolean,
): boolean {
  let routeInfo = currentRoute
  while (routeInfo) {
    if (predicate(routeInfo)) {
      return true
    }
    routeInfo = routeInfo.parent
  }
  return false
}
